/**
 * Digest-verified artifact reads, layered as three caches (owner
 * requirement 2026-09-26, requirement 2): an in-memory LRU of *decoded*
 * values (L1), IndexedDB of decoded values keyed by sha256 (L2, survives a
 * refresh), and the network as the last resort — which itself goes through
 * a digest-verified raw-bytes cache so a cache-miss on the decoded value
 * still avoids re-verifying bytes already fetched this session.
 *
 * Mirrors the real content-addressed blob layout on the browser side. Two
 * shapes, chosen by the caller (`src/data/snapshotIndex.ts`) per contract
 * version:
 * - v1: `<versionDir>/artifacts/objects/<sha256[:2]>/<sha256>`
 *   (`organism.observatory.publish._blob_path`) — two-level fan-out.
 * - v2: `<roleRoot>/objects/<sha256>` — flat, no fan-out, no per-version
 *   directory (verified live against the real host 2026-09-26: `objects/`
 *   holds every blob directly by full digest).
 * Every network read verifies the fetched bytes against the manifest's own
 * `sha256`/`size` before caching or decoding — a mismatch is a refusal
 * (`ArtifactIntegrityError`), never a silent pass-through.
 */

import type { ArtifactRef } from "../types/snapshot";
import type { RawContentRef } from "../types/incremental";
import { fetchBytes } from "./fetchJson";
import { verifySha256 } from "./hash";
import { parseNpy, type NpyArray } from "./codecs/npy";
import { parseNpz } from "./codecs/npz";
import {
  decodeActivityGrid,
  dequantizeActivityGrid,
  type ActivityGrid,
} from "./codecs/zlibActivity";
import { LruCache } from "../cache/LruCache";
import { idbGetArtifact, idbPutArtifact, isIndexedDbAvailable } from "../cache/idb";
import { getSharedDecodeClient } from "./workerDecodeClient";

/** Decodes off the main thread via the shared worker when one is available
 * (owner requirement 2026-09-26, requirement 4), falling back to the local
 * synchronous codec otherwise (no `Worker` global — every non-browser test
 * environment, and any browser old enough to lack module workers). */
async function decodeOffThreadOrLocally<T>(
  kind: "npy" | "npz" | "activity",
  bytes: ArrayBuffer,
  shape: readonly number[] | undefined,
  localDecode: () => Promise<T>,
): Promise<T> {
  const client = getSharedDecodeClient();
  if (!client) return localDecode();
  try {
    return await client.decode<T>(kind, bytes, shape);
  } catch {
    // A worker-side failure (e.g. the module worker failed to start under
    // this bundler/host) degrades to the main thread rather than losing the
    // artifact entirely.
    return localDecode();
  }
}

export class ArtifactDecodeError extends Error {}

/** Runs `run()` at most once per `key` concurrently outstanding on `map` —
 * a second caller for the same key while the first is still in flight
 * awaits that same promise instead of starting a second one (2026-09-28
 * fix, see `ArtifactStore.inFlightBytes`'s doc comment for the concrete
 * bug this fixes). Shared by `getBytesByDigest` (raw bytes) and
 * `getDecodedByDigest` (decoded values) so the check-map / run / set /
 * try-finally-delete dance lives in exactly one place instead of two
 * hand-copied implementations that could drift apart (code-review
 * finding). */
async function singleFlight<T>(
  // `Map<string, Promise<unknown>>`, not `Map<string, Promise<T>>` (code-
  // review finding): `ArtifactStore.inFlightDecoded` is one map shared
  // across every decoded kind (npy/npz/activity), each with its own `T`,
  // erased to `unknown` — a `Map<string, Promise<T>>` parameter would force
  // that call site to cast the *entire map* to fit one call's `T`, widening
  // the type-unsoundness to every read/write `singleFlight` performs
  // instead of just the one value this function already has to trust its
  // caller about.
  map: Map<string, Promise<unknown>>,
  key: string,
  run: () => Promise<T>,
): Promise<T> {
  const inFlight = map.get(key);
  if (inFlight) return inFlight as Promise<T>;

  const promise = run();
  map.set(key, promise);
  try {
    return await promise;
  } finally {
    // Cleared whether `run()` succeeded or failed — a failure must not
    // permanently wedge later callers behind a rejected promise.
    map.delete(key);
  }
}

function objectPath(root: string, objectsSubpath: string, sha256: string, flat: boolean): string {
  // `root` may or may not already end in "/" depending on the caller (both
  // conventions exist in snapshotIndex.ts) — normalize here so this is the
  // one place a mismatch can't produce a "//objects/..." URL. Most static
  // file servers (and nginx's default `merge_slashes on`) tolerate a
  // doubled slash, but it is not something to rely on.
  const normalizedRoot = root.endsWith("/") ? root.slice(0, -1) : root;
  if (flat) return `${normalizedRoot}/${objectsSubpath}/${sha256}`;
  const prefix = sha256.slice(0, 2);
  return `${normalizedRoot}/${objectsSubpath}/${prefix}/${sha256}`;
}

interface RawCacheEntry {
  readonly bytes: ArrayBuffer;
}

const MAX_RAW_CACHE_ENTRIES = 64;
const MAX_DECODED_CACHE_ENTRIES = 128;

/** LRU-backed, digest-verified artifact store with an IndexedDB-backed L2
 * for decoded values (requirement 2b/2c). Bounded so a long scrubbing
 * session cannot grow memory unboundedly (§B.6 "60 fps ... on Apple
 * silicon" implies a real memory budget, not just a frame-time one). */
export class ArtifactStore {
  private readonly rawCache = new Map<string, RawCacheEntry>();
  private readonly decodedCache = new LruCache<string, unknown>(MAX_DECODED_CACHE_ENTRIES);
  /** In-flight request dedup (2026-09-28 fix): two panels reading the same
   * digest in the same tick — e.g. `PortfolioPanel` and `VitalsHero` both
   * call `role.equityCurve()` on mount, which both resolve to the same
   * `performance` artifact's sha256 — used to each issue their own network
   * fetch, because the raw-bytes cache is only populated *after* a fetch
   * resolves; two callers racing ahead of that first resolution both saw a
   * cache miss. Keyed the same way as the cache it backs (bare sha256 here,
   * `kind:sha256` in `inFlightDecoded`), so a second caller within the same
   * macrotask awaits the first caller's promise instead of starting a
   * second fetch/decode. */
  private readonly inFlightBytes = new Map<string, Promise<ArrayBuffer>>();
  private readonly inFlightDecoded = new Map<string, Promise<unknown>>();

  /** `objectsSubpath`/`flat` default to the v1 shape (`"artifacts/objects"`,
   * two-level fan-out); `src/data/snapshotIndex.ts` passes `("objects",
   * true)` for a v2 role (flat, verified live 2026-09-26). */
  constructor(
    readonly versionDir: string,
    private readonly objectsSubpath: string = "artifacts/objects",
    private readonly flat: boolean = false,
  ) {}

  private urlForDigest(sha256: string): string {
    return objectPath(this.versionDir, this.objectsSubpath, sha256, this.flat);
  }

  /** Fetches + verifies raw bytes for one digest, using the L1 raw-bytes
   * cache. The generic primitive both `getBytes` (v1 `ArtifactRef`) and
   * `getRawNpy` (v2 `RawContentRef`) build on. */
  async getBytesByDigest(sha256: string, expectedSize?: number): Promise<ArrayBuffer> {
    const cached = this.rawCache.get(sha256);
    if (cached) {
      this.rawCache.delete(sha256);
      this.rawCache.set(sha256, cached);
      return cached.bytes;
    }

    return singleFlight(this.inFlightBytes, sha256, async () => {
      const url = this.urlForDigest(sha256);
      // OQA: a fixed 10s timeout failed the 2 MB MCNS layout on a slow link (the
      // brain then never rendered). Budget >= 50 kB/s for the expected size.
      const timeoutMs =
        expectedSize !== undefined
          ? Math.max(10_000, Math.ceil(expectedSize / 50_000) * 1_000)
          : 30_000;
      const bytes = await fetchBytes(url, timeoutMs);
      if (expectedSize !== undefined && bytes.byteLength !== expectedSize) {
        throw new ArtifactDecodeError(
          `artifact at ${url} is ${bytes.byteLength} bytes, manifest says ${expectedSize}`,
        );
      }
      await verifySha256(bytes, sha256, url);
      this.rawCache.set(sha256, { bytes });
      if (this.rawCache.size > MAX_RAW_CACHE_ENTRIES) {
        const oldestKey = this.rawCache.keys().next().value;
        if (oldestKey !== undefined) this.rawCache.delete(oldestKey);
      }
      return bytes;
    });
  }

  /** Fetches + verifies one artifact's raw bytes (v1 `ArtifactRef` shape),
   * using the L1 raw-bytes cache. Bypasses the decoded-value caches
   * entirely — most callers want `getNpy`/`getNpz`/`getActivityGrid`
   * instead. */
  async getBytes(ref: ArtifactRef): Promise<ArrayBuffer> {
    return this.getBytesByDigest(ref.sha256, ref.size);
  }

  /** Shared L1(memory)/L2(IndexedDB)/network layering for a decoded value,
   * keyed by digest — content-addressed, so a hit at any layer is always
   * correct regardless of which publish produced it. */
  private async getDecodedByDigest<T>(
    sha256: string,
    kind: "npy" | "npz" | "activity",
    decode: () => Promise<T>,
  ): Promise<T> {
    const cacheKey = `${kind}:${sha256}`;
    const memoryHit = this.decodedCache.get(cacheKey);
    if (memoryHit !== undefined) return memoryHit as T;

    return singleFlight(this.inFlightDecoded, cacheKey, async () => {
      if (isIndexedDbAvailable()) {
        try {
          const idbHit = await idbGetArtifact(sha256);
          if (idbHit && idbHit.kind === kind) {
            this.decodedCache.set(cacheKey, idbHit.decoded);
            return idbHit.decoded as T;
          }
        } catch {
          // IDB errors (quota, private-browsing restrictions, ...) degrade to
          // "no L2 cache" — never block the actual data path.
        }
      }

      const decoded = await decode();
      this.decodedCache.set(cacheKey, decoded);
      if (isIndexedDbAvailable()) {
        idbPutArtifact({ sha256, kind, decoded, storedAt: Date.now() }).catch(() => {
          // Best-effort persistence only.
        });
      }
      return decoded;
    });
  }

  /** Decodes a `codec: "npy"` artifact (v1 `ArtifactRef` shape — dtype/shape
   * carried alongside the digest, e.g. a `brain.wake` event's `*_ref`
   * fields). */
  async getNpy(ref: ArtifactRef): Promise<NpyArray> {
    if (ref.codec !== "npy") {
      throw new ArtifactDecodeError(`ref codec ${ref.codec} is not "npy"`);
    }
    return this.getDecodedByDigest(ref.sha256, "npy", async () => {
      const bytes = await this.getBytes(ref);
      return decodeOffThreadOrLocally("npy", bytes, undefined, () =>
        Promise.resolve(parseNpy(bytes)),
      );
    });
  }

  /** Decodes a `.npy` artifact addressed by a v2 `RawContentRef`
   * (`{algorithm, digest, size}`, no dtype/shape/codec — self-describing:
   * the npy header carries its own dtype/shape, verified live against the
   * real MCNS layout artifacts on 2026-09-26). Used for the OBS-2 layout
   * manifest's `artifacts.{xyz,region_id,class_id,sensory_pool_id}`. */
  async getRawNpy(ref: RawContentRef): Promise<NpyArray> {
    if ((ref.algorithm as string) !== "sha256") {
      throw new ArtifactDecodeError(`unsupported artifact algorithm ${String(ref.algorithm)}`);
    }
    return this.getDecodedByDigest(ref.digest, "npy", async () => {
      const bytes = await this.getBytesByDigest(ref.digest, ref.size);
      return decodeOffThreadOrLocally("npy", bytes, undefined, () =>
        Promise.resolve(parseNpy(bytes)),
      );
    });
  }

  /** Decodes a `codec: "npz"` artifact into its named `.npy` members. */
  async getNpz(ref: ArtifactRef): Promise<Record<string, NpyArray>> {
    if (ref.codec !== "npz") {
      throw new ArtifactDecodeError(`ref codec ${ref.codec} is not "npz"`);
    }
    return this.getDecodedByDigest(ref.sha256, "npz", async () => {
      const bytes = await this.getBytes(ref);
      return decodeOffThreadOrLocally("npz", bytes, undefined, () => parseNpz(bytes));
    });
  }

  /** Decodes a `codec: "zlib_uint8"` activity artifact into its raw grid. */
  async getActivityGrid(ref: ArtifactRef): Promise<ActivityGrid> {
    if (ref.codec !== "zlib_uint8") {
      throw new ArtifactDecodeError(`ref codec ${ref.codec} is not "zlib_uint8"`);
    }
    return this.getDecodedByDigest(ref.sha256, "activity", async () => {
      const bytes = await this.getBytes(ref);
      return decodeOffThreadOrLocally("activity", bytes, ref.shape, () =>
        decodeActivityGrid(bytes, ref.shape),
      );
    });
  }

  /** Convenience: decodes a `zlib_uint8` artifact straight to dequantized floats. */
  async getActivityFloats(ref: ArtifactRef): Promise<Float32Array> {
    return dequantizeActivityGrid(await this.getActivityGrid(ref));
  }

  /** Fetches + parses a plain JSON object by digest (v2's layout manifest,
   * taxonomy, tooltip KB — every `ContentAddress`-pointed document). Not
   * cached in the decoded-value LRU/IDB (these are typically fetched once
   * per session, and re-parsing a small JSON document is cheap); the raw
   * bytes still go through the L1 raw cache via `getBytesByDigest`. */
  async getJsonByDigest<T>(sha256: string): Promise<T> {
    const bytes = await this.getBytesByDigest(sha256);
    return JSON.parse(new TextDecoder("utf-8").decode(bytes)) as T;
  }
}
